import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Trash2, Plus } from "lucide-react";
import { useV2List, useV2Save, useV2Delete } from "@/hooks/useTransfersV2";

export interface CrudField {
  key: string;
  label: string;
  type?: "text" | "number" | "textarea" | "boolean" | "select" | "json";
  options?: { value: string; label: string }[];
  placeholder?: string;
  required?: boolean;
  step?: string;
}

interface CrudTableProps {
  table: any;
  fields: CrudField[];
  displayColumns: string[];
  title: string;
  renderCell?: (row: any, col: string) => React.ReactNode;
}

export default function CrudTable({ table, fields, displayColumns, title, renderCell }: CrudTableProps) {
  const { data = [], isLoading } = useV2List(table, true);
  const save = useV2Save(table);
  const del = useV2Delete(table);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState<any>({});

  const openNew = () => {
    setEditing(null);
    const initial: any = {};
    fields.forEach((f) => {
      initial[f.key] = f.type === "boolean" ? true : f.type === "number" ? 0 : f.type === "json" ? "[]" : "";
    });
    setForm(initial);
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    const clone: any = { ...row };
    fields.forEach((f) => {
      if (f.type === "json") clone[f.key] = JSON.stringify(clone[f.key] ?? [], null, 2);
    });
    setForm(clone);
    setOpen(true);
  };

  const submit = async () => {
    const values: any = {};
    for (const f of fields) {
      let v = form[f.key];
      if (f.type === "number") v = v === "" || v === null ? null : Number(v);
      if (f.type === "json") {
        try {
          v = JSON.parse(v || "[]");
        } catch {
          alert(`Invalid JSON in ${f.label}`);
          return;
        }
      }
      if (f.type === "boolean") v = Boolean(v);
      values[f.key] = v;
    }
    await save.mutateAsync({ id: editing?.id, values });
    setOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">{title}</h2>
        <Button onClick={openNew} className="gap-2">
          <Plus className="h-4 w-4" /> New
        </Button>
      </div>

      <div className="rounded-lg border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {displayColumns.map((c) => (
                <TableHead key={c}>{c}</TableHead>
              ))}
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={displayColumns.length + 1} className="text-center py-8">
                  Loading...
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={displayColumns.length + 1} className="text-center py-8 text-muted-foreground">
                  No records yet
                </TableCell>
              </TableRow>
            ) : (
              data.map((row: any) => (
                <TableRow key={row.id}>
                  {displayColumns.map((c) => (
                    <TableCell key={c}>
                      {renderCell ? renderCell(row, c) : String(row[c] ?? "-")}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => confirm("Delete this record?") && del.mutate(row.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "New"} {title}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {fields.map((f) => (
              <div key={f.key} className={f.type === "textarea" || f.type === "json" ? "md:col-span-2" : ""}>
                <Label>{f.label}{f.required && " *"}</Label>
                {f.type === "textarea" ? (
                  <Textarea
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    rows={3}
                  />
                ) : f.type === "json" ? (
                  <Textarea
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder='["item1","item2"]'
                    rows={4}
                    className="font-mono text-xs"
                  />
                ) : f.type === "boolean" ? (
                  <div className="flex items-center pt-2">
                    <Switch
                      checked={Boolean(form[f.key])}
                      onCheckedChange={(v) => setForm({ ...form, [f.key]: v })}
                    />
                  </div>
                ) : f.type === "select" ? (
                  <select
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  >
                    <option value="">-- Select --</option>
                    {f.options?.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : "text"}
                    step={f.step}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={save.isPending}>
              {save.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
